import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Input } from 'src/design/atoms/Input';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import React from 'react';
import { JobUserSelectV2 } from 'src/app/selectors/JobUserSelectV2';
import { RoleUserSelector } from './RoleUserSelector';
import { ColorSelect, IntentType } from 'src/app/selectors/ColorSelect';
import type { WorkflowStepDto } from './EditDrawer';

export interface IEditDrawerSettingsTabProps {
  current: WorkflowStepDto;
  isTemplateEditor: boolean;
  resourceId?: string;
  onFieldChange: (field: string, value: any) => void;
  onValueChange: (step: WorkflowStepDto) => void;
  onButtonTextUpdated: (value: string) => void;
}

/** @registry General-settings tab of a workflow step's EditDrawer — button text/color, step behavior flags, assignment roles/users. */
export const EditDrawerSettingsTab: React.FC<IEditDrawerSettingsTabProps> = ({
  current,
  isTemplateEditor,
  resourceId,
  onFieldChange,
  onValueChange,
  onButtonTextUpdated,
}) => {
  return (
    <Stack gap="lg">
      <Title order={3}>General Settings</Title>

      <SimpleGrid cols={{ base: 2 }}>
        <TextInput
          label="Button Text"
          description="Text that the user will see when transitioning to this status"
          value={current.buttonText}
          onChange={(e) => onButtonTextUpdated(e.target.value)}
        />

        <Input.Wrapper
          label="Button Color"
          description="Color of the create button"
        >
          <ColorSelect
            onChange={(value) => onFieldChange('buttonColor', value)}
            value={(current.buttonColor as IntentType) ?? 'primary'}
            hideLabel
          />
        </Input.Wrapper>
      </SimpleGrid>

      <TextInput
        label="Display Label"
        description="Shown to users in tabs, badges, and reports. Leave blank to use the status label."
        value={current.displayLabel ?? ''}
        onChange={(e) => onFieldChange('displayLabel', e.target.value || null)}
      />

      <Textarea
        label="Description"
        description="Help text shown to guide users at this step."
        value={current.description ?? ''}
        onChange={(e) => onFieldChange('description', e.target.value || null)}
        autosize
        minRows={2}
      />

      <Stack gap="xs">
        <Checkbox
          checked={current.isExternalStep}
          label="Assign / Send to Client"
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            if (checked) {
              const updated = {
                ...current,
                createPdf: true,
                attachPdfToNotification: true,
                isExternalStep: true,
                allowVoid: false,
              };
              onValueChange(updated);
            } else {
              onFieldChange('isExternalStep', checked);
            }
          }}
        />
        {current.isExternalStep && (
          <Checkbox
            ml="md"
            checked={true}
            label="Contact selected prior to status transition"
            disabled
          />
        )}

        <Checkbox
          disabled={current.isExternalStep}
          checked={current.allowVoid}
          label="Allow void from status"
          onChange={(e) => onFieldChange('allowVoid', e.currentTarget.checked)}
        />

        {!current.isLastStep && (
          <Checkbox
            checked={current.allowReject}
            label="Allow reject to previous status"
            onChange={(e) =>
              onFieldChange('allowReject', e.currentTarget.checked)
            }
          />
        )}

        {!current.isLastStep && (
          <Checkbox
            checked={current.allowRevise}
            label="Allow revise to draft"
            onChange={(e) =>
              onFieldChange('allowRevise', e.currentTarget.checked)
            }
          />
        )}

        <Checkbox
          disabled={current.isExternalStep}
          checked={current.createPdf}
          label="Create .pdf upon status transition"
          onChange={(e) => {
            onFieldChange('createPdf', e.currentTarget.checked);
          }}
        />
        {current.createPdf && (
          <Checkbox
            ml="md"
            disabled={current.isExternalStep}
            checked={current.attachPdfToNotification}
            label="Attach .pdf to notification"
            onChange={(e) =>
              onFieldChange('attachPdfToNotification', e.currentTarget.checked)
            }
          />
        )}

        {resourceId === 'timesheet' && (
          <>
            <Checkbox
              disabled={
                current.isExternalStep || current.isSupervisorReviewStep
              }
              checked={current.isEmployeeReviewStep}
              label="Items will be sent to employee for review"
              onChange={(e) =>
                onFieldChange('isEmployeeReviewStep', e.currentTarget.checked)
              }
            />

            <Checkbox
              disabled={current.isExternalStep || current.isEmployeeReviewStep}
              checked={current.isSupervisorReviewStep}
              label="Items will be sent to supervisor for review"
              onChange={(e) =>
                onFieldChange('isSupervisorReviewStep', e.currentTarget.checked)
              }
            />
          </>
        )}

        <Checkbox
          checked={current.allowOwner}
          label="Allow resource owner to perform action"
          onChange={(e) => onFieldChange('allowOwner', e.currentTarget.checked)}
        />

        <Checkbox
          checked={current.editable ?? true}
          label="Allow data edits at this step"
          description="When unchecked, data inputs are read-only but workflow transitions remain available"
          onChange={(e) => onFieldChange('editable', e.currentTarget.checked)}
        />
      </Stack>

      {!current.isExternalStep &&
        !current.isLastStep &&
        !current.isEmployeeReviewStep &&
        !current.isSupervisorReviewStep && (
          <>
            <Title order={3}>Assignments</Title>
            <SimpleGrid cols={{ base: 1, lg: 2 }}>
              <Input.Wrapper
                label="Assign to Roles"
                description="Users with selected roles will be assigned items with this status. Leave blank if final step."
              >
                <RoleUserSelector
                  field={'assignedRoles'}
                  initialUsers={current.excludedAssignedUsers.map(
                    (user) => user.jobUser,
                  )}
                  onUserSelect={(val) =>
                    onFieldChange(
                      'excludedAssignedUsers',
                      val.map((user) => ({
                        jobUser: user,
                        jobUserId: user.id,
                      })),
                    )
                  }
                  placeholder="Select assigned roles..."
                  initialSelectedRoles={current.assignedRoles.map(
                    (role) => role.organizationRole,
                  )}
                  selectedRoles={current.assignedRoles.map(
                    (role) => role.organizationRole,
                  )}
                  selectDescription="Select users to EXCLUDE from assigned roles"
                  onFieldChange={(field, value) =>
                    onFieldChange(
                      field,
                      value.map((role) => ({
                        organizationRoleId: role.id,
                        organizationRole: role,
                      })),
                    )
                  }
                />
              </Input.Wrapper>

              <Input.Wrapper
                hidden={isTemplateEditor}
                label="Assign to Users"
                description="Selected users will also be assigned items with this status regardless of user roles"
              >
                <JobUserSelectV2
                  hidden={isTemplateEditor}
                  onChange={(value) =>
                    onFieldChange(
                      'assignedUsers',
                      value.map((user) => ({
                        jobUserId: user.id,
                        jobUser: user,
                      })),
                    )
                  }
                  value={current.assignedUsers.map((user) => user.jobUser)}
                  hideLabel
                />
              </Input.Wrapper>
            </SimpleGrid>
          </>
        )}

      {current.isEmployeeReviewStep && (
        <Text>Items will be sent to employee for review</Text>
      )}
      {current.isSupervisorReviewStep && (
        <Text>Items will be sent to supervisor for review</Text>
      )}
      {current.isExternalStep && <Text>Items assigned to external client</Text>}
      {current.isLastStep && (
        <Text>Items with this status are considered complete</Text>
      )}
    </Stack>
  );
};
